/**
 * Runtime-tunable configuration for an ElevenLabs Scribe streaming session.
 *
 * Audio constraints (passed to Scribe.connect → microphone options):
 *   echoCancellation, noiseSuppression, autoGainControl
 *
 * VAD (Voice Activity Detection) parameters control how Scribe segments
 * speech into committed transcripts. Tuning these affects segment granularity
 * and can approximate turn-based splitting in multi-speaker scenarios.
 *
 * Silence detection parameters (silenceTimeoutMs, silenceWarningMs) control
 * the transcription-result silence timer in useTranscriptionSilenceTimer.
 * They have no effect on raw audio — see that module for the rationale.
 *
 * This config only includes options supported by Scribe. The SDK manages audio
 * capture internally and does not expose partial-results stabilization settings.
 */
export type ElevenLabsTranscriptionConfig = {
   echoCancellation: boolean;
   noiseSuppression: boolean;
   autoGainControl: boolean;
   /**
    * How long continuous absence of transcript events must persist before
    * the session is disconnected. Implemented in useTranscriptionSilenceTimer.
    */
   silenceTimeoutMs: number;
   /**
    * How many milliseconds before the timeout to start showing the countdown warning.
    * E.g. silenceTimeoutMs=30000, silenceWarningMs=10000 → warning appears after 20s.
    */
   silenceWarningMs: number;
   /**
    * VAD silence threshold in seconds (0.3–3.0).
    * How long silence must last for VAD to commit a segment.
    * Lower = more frequent commits = smaller segments (better pseudo-turn detection).
    */
   vadSilenceThresholdSecs: number;
   /**
    * VAD sensitivity threshold (0.1–0.9).
    * Higher = less sensitive to quiet speech / background noise.
    */
   vadThreshold: number;
   /**
    * Minimum speech duration in ms (50–2000) to be recognized as a segment.
    * Filters out very short sounds (coughs, clicks).
    */
   minSpeechDurationMs: number;
   /**
    * Minimum silence duration in ms (50–2000) to end a segment.
    * Lower = faster segmentation, more responsive turn detection.
    */
   minSilenceDurationMs: number;
   /**
    * Enable background speech filtering to reduce false activations from
    * nearby conversations and ambient noise.
    */
   filterBackgroundAudio: boolean;
   /**
    * Prime websocket with few seconds of data
    */
   lowLatency: boolean;
};

export const DEFAULT_ELEVENLABS_CONFIG: ElevenLabsTranscriptionConfig = {
   echoCancellation: true,
   noiseSuppression: true,
   autoGainControl: true,
   silenceTimeoutMs: 30_000,
   silenceWarningMs: 10_000,
   vadSilenceThresholdSecs: 1.5,
   vadThreshold: 0.4,
   minSpeechDurationMs: 100,
   minSilenceDurationMs: 100,
   lowLatency: true,
   filterBackgroundAudio: false,
};

/**
 * Config used when diarization (showSpeakerLabel) is active.
 * Echo/noise/gain disabled to preserve speaker vocal fingerprints.
 */
export const DEFAULT_ELEVENLABS_DIARIZATION_CONFIG: ElevenLabsTranscriptionConfig = {
   echoCancellation: true,
   noiseSuppression: false,
   autoGainControl: true,
   silenceTimeoutMs: 30_000,
   silenceWarningMs: 10_000,
   vadSilenceThresholdSecs: 1.0,
   vadThreshold: 0.3,
   minSpeechDurationMs: 100,
   minSilenceDurationMs: 100,
   lowLatency: false,
   filterBackgroundAudio: true,
};
